import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FieldError } from '../../../../../components/novarium/NvTableParts';
import useNotify from '../../../../../hooks/useNotify';
import { parseApiError } from '../../../../../services/apiError';
import { formatMoney } from '../../../../../utils/money';
import { associateOrdersToAppointment, getPatientOperations } from '../../../services/AgendaService';
import { PatientOperation } from '../../../types/billing.types';
import { errorMessageOf, formatShortDate } from '../billingUtils';

interface AssociateOrdersDialogProps {
  appointmentId: number;
  patientName: string;
  onCancel: () => void;
  onAssociated: () => void;
}

/** "Asociar": órdenes ya registradas del paciente que se asocian al turno, como en el registro de órdenes de Vaadin. */
export default function AssociateOrdersDialog({ appointmentId, patientName, onCancel, onAssociated }: AssociateOrdersDialogProps) {
  const notify = useNotify();
  const [operations, setOperations] = useState<PatientOperation[] | null>(null);
  const [selected, setSelected] = useState<PatientOperation[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPatientOperations(appointmentId)
      .then((data) => { if (!cancelled) setOperations(data); })
      .catch((e: unknown) => {
        if (cancelled) return;
        notify.error(errorMessageOf(parseApiError(e), 'No se pudieron cargar las órdenes del paciente. Intentá de nuevo.'));
        setOperations([]);
      });
    return () => { cancelled = true; };
  }, [appointmentId, notify]);

  async function associate() {
    if (selected.length === 0) {
      setError('Elegí al menos una orden');
      return;
    }
    setSaving(true);
    try {
      await associateOrdersToAppointment(appointmentId, selected.map((o) => o.operationId));
      notify.success(selected.length > 1 ? 'Órdenes asociadas' : 'Orden asociada');
      onAssociated();
    } catch (e: unknown) {
      notify.error(errorMessageOf(parseApiError(e), 'No se pudieron asociar las órdenes. Intentá de nuevo.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      header={`Órdenes de prestación · ${patientName}`}
      visible
      onHide={onCancel}
      className="nv-scope nv-dialog nv-dialog-l"
      modal
      draggable={false}
      footer={(
        <>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onCancel} disabled={saving} />
          <Button type="button" className="buttonMain" label="Asociar" onClick={associate} loading={saving} />
        </>
      )}
    >
      <div className="nv-dialog-body">
        {operations === null ? (
          <div className="nv-loading" role="status"><ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando…</div>
        ) : (
          <>
            <DataTable
              value={operations}
              dataKey="operationId"
              selectionMode="multiple"
              selection={selected}
              onSelectionChange={(e) => { setSelected((e.value as PatientOperation[]) ?? []); setError(undefined); }}
              emptyMessage="El paciente no tiene órdenes para asociar."
              scrollable
              scrollHeight="420px"
            >
              <Column selectionMode="multiple" className="nv-col-check" />
              <Column header="Nro. operación" field="operationId" className="nv-col-qty" align="center" alignHeader="center" />
              <Column header="Registro" body={(o: PatientOperation) => (o.recordDateTime ? new Date(o.recordDateTime).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—')} />
              <Column header="Modalidad" body={(o: PatientOperation) => o.modality || '—'} />
              <Column header="Autorización" body={(o: PatientOperation) => o.authorizations || '—'} />
              <Column header="Fecha de la orden" className="nv-col-date" body={(o: PatientOperation) => formatShortDate(o.orderDate)} />
              <Column header="Registrado por" body={(o: PatientOperation) => o.registeredBy || '—'} />
              <Column header="Obra social" body={(o: PatientOperation) => o.coverage || '—'} />
              <Column header="Plan" body={(o: PatientOperation) => o.plan || '—'} />
              <Column header="Prestaciones" body={(o: PatientOperation) => o.benefits || '—'} />
              <Column header="Importe total" className="nv-col-money" align="right" alignHeader="right" body={(o: PatientOperation) => formatMoney(o.amount)} />
              <Column header="Observaciones" body={(o: PatientOperation) => o.observations || '—'} />
              <Column header="Asociaciones previas" body={(o: PatientOperation) => o.previousAssociations || '—'} />
            </DataTable>
            <FieldError id="associate-error" message={error} />
          </>
        )}
      </div>
    </Dialog>
  );
}
